import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './InstructorSignup.css';

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

/**
 * InstructorSignup — Pre-registration gate before the ADI Application form.
 *
 * Step 1 — Collect email + password, send verification code
 * Step 2 — Enter 6-digit verification code
 * Step 3 — Redirect to /apply with a registration token stored in sessionStorage
 */

function PasswordStrength({ password }) {
  if (!password) return null;
  let strength = 0;
  if (password.length >= 8) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[^A-Za-z0-9]/.test(password)) strength++;

  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const colours = ['', '#ef4444', '#f59e0b', '#22c55e', '#16a34a'];

  return (
    <div className="is-pw-strength">
      <div className="is-pw-bars">
        {[1, 2, 3, 4].map(i => (
          <div
            key={i}
            className="is-pw-bar"
            style={{ background: i <= strength ? colours[strength] : '#e2e8f0' }}
          />
        ))}
      </div>
      <span className="is-pw-label" style={{ color: colours[strength] }}>{labels[strength]}</span>
    </div>
  );
}

export default function InstructorSignup() {
  const navigate = useNavigate();

  // ── Step state ──
  const [step, setStep] = useState(1); // 1 = register, 2 = verify

  // ── Step 1 fields ──
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [step1Errors, setStep1Errors] = useState({});
  const [step1Loading, setStep1Loading] = useState(false);

  // ── Step 2 fields ──
  const [code, setCode] = useState('');
  const [step2Errors, setStep2Errors] = useState({});
  const [step2Loading, setStep2Loading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // ── Shared ──
  const [serverError, setServerError] = useState('');

  // ─────────────── STEP 1 ───────────────
  const validateStep1 = () => {
    const e = {};
    if (!email.trim()) {
      e.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      e.email = 'Enter a valid email address';
    }
    if (!password) {
      e.password = 'Password is required';
    } else if (password.length < 8) {
      e.password = 'Password must be at least 8 characters';
    }
    if (!confirmPassword) {
      e.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      e.confirmPassword = 'Passwords do not match';
    }
    setStep1Errors(e);
    return Object.keys(e).length === 0;
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setServerError('');
    if (!validateStep1()) return;

    setStep1Loading(true);
    try {
      await api.post('/instructor-auth/pre-register', {
        email: email.toLowerCase().trim(),
        password,
      });
      setStep(2);
      startResendCooldown(60);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Registration failed. Please try again.';
      setServerError(msg);
    } finally {
      setStep1Loading(false);
    }
  };

  // ─────────────── STEP 2 ───────────────
  const startResendCooldown = (seconds) => {
    setResendCooldown(seconds);
    const timer = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setStep2Errors({});
    setServerError('');

    const trimmedCode = code.replace(/\s/g, '');
    if (!trimmedCode || trimmedCode.length !== 6 || !/^\d{6}$/.test(trimmedCode)) {
      setStep2Errors({ code: 'Please enter the 6-digit code from your email' });
      return;
    }

    setStep2Loading(true);
    try {
      const data = await api.post('/instructor-auth/verify-email', {
        email: email.toLowerCase().trim(),
        code: trimmedCode,
      });

      // Store registration token so ADIFormContainer can include it on submission
      sessionStorage.setItem('instructorRegToken', data.registrationToken);
      sessionStorage.setItem('instructorPreEmail', email.toLowerCase().trim());

      // Brief success pause then navigate
      setStep(3);
      setTimeout(() => navigate('/apply'), 1800);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Invalid or expired code. Please try again.';
      setServerError(msg);
    } finally {
      setStep2Loading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setServerError('');
    try {
      await api.post('/instructor-auth/pre-register', {
        email: email.toLowerCase().trim(),
        password,
        resend: true,
      });
      startResendCooldown(60);
    } catch (err) {
      setServerError('Could not resend code. Please try again.');
    }
  };

  // ─────────────── RENDER ───────────────
  return (
    <div className="is-page">

      {/* Hero */}
      <div className="is-hero">
        <div className="is-hero-inner">
          <span className="is-eyebrow">🇬🇧 Become an Instructor</span>
          <h1>Create Your Instructor Account</h1>
          <p>Set up your account first so you can track and update your application at any time.</p>
          {/* Progress dots */}
          <div className="is-progress">
            <div className={`is-progress-step${step >= 1 ? ' is-progress-step--done' : ''}`}>
              <span className="is-step-num">1</span>
              <span className="is-step-label">Create Account</span>
            </div>
            <div className="is-progress-line" />
            <div className={`is-progress-step${step >= 2 ? ' is-progress-step--done' : ''}`}>
              <span className="is-step-num">2</span>
              <span className="is-step-label">Verify Email</span>
            </div>
            <div className="is-progress-line" />
            <div className={`is-progress-step${step >= 3 ? ' is-progress-step--done' : ''}`}>
              <span className="is-step-num">3</span>
              <span className="is-step-label">Start Application</span>
            </div>
          </div>
        </div>
      </div>

      {/* Card */}
      <div className="is-layout">
        <div className="is-card">

          {/* ── STEP 1: Register ── */}
          {step === 1 && (
            <>
              <div className="is-card-header">
                <div className="is-card-icon">🔐</div>
                <h2>Create Your Account</h2>
                <p>Your email and password will be used to log in to the instructor portal later.</p>
              </div>

              <form onSubmit={handleStep1Submit} className="is-form" noValidate>

                <div className={`is-field${step1Errors.email ? ' is-field--err' : ''}`}>
                  <label htmlFor="is-email">Email Address *</label>
                  <input
                    id="is-email"
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setStep1Errors(prev => ({ ...prev, email: '' })); }}
                    placeholder="your.email@example.com"
                    autoComplete="email"
                  />
                  {step1Errors.email && <span className="is-err">{step1Errors.email}</span>}
                </div>

                <div className={`is-field${step1Errors.password ? ' is-field--err' : ''}`}>
                  <label htmlFor="is-password">Password *</label>
                  <div className="is-pw-wrap">
                    <input
                      id="is-password"
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={e => { setPassword(e.target.value); setStep1Errors(prev => ({ ...prev, password: '' })); }}
                      placeholder="Minimum 8 characters"
                      autoComplete="new-password"
                    />
                    <button type="button" className="is-pw-toggle" onClick={() => setShowPw(v => !v)} tabIndex={-1} aria-label={showPw ? 'Hide password' : 'Show password'}>
                      {showPw ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                  <PasswordStrength password={password} />
                  {step1Errors.password && <span className="is-err">{step1Errors.password}</span>}
                </div>

                <div className={`is-field${step1Errors.confirmPassword ? ' is-field--err' : ''}`}>
                  <label htmlFor="is-confirm">Confirm Password *</label>
                  <div className="is-pw-wrap">
                    <input
                      id="is-confirm"
                      type={showPw ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => { setConfirmPassword(e.target.value); setStep1Errors(prev => ({ ...prev, confirmPassword: '' })); }}
                      placeholder="Re-enter your password"
                      autoComplete="new-password"
                    />
                  </div>
                  {step1Errors.confirmPassword && <span className="is-err">{step1Errors.confirmPassword}</span>}
                </div>

                {serverError && <div className="is-server-error">⚠ {serverError}</div>}

                <button type="submit" className="is-btn is-btn--primary" disabled={step1Loading}>
                  {step1Loading ? <><span className="is-spinner" /> Sending Code…</> : 'Send Verification Code →'}
                </button>

                <p className="is-login-link">
                  Already have an account?{' '}
                  <a href="/instructor-login">Log in to Instructor Portal</a>
                </p>

              </form>
            </>
          )}

          {/* ── STEP 2: Verify Email ── */}
          {step === 2 && (
            <>
              <div className="is-card-header">
                <div className="is-card-icon">📧</div>
                <h2>Verify Your Email</h2>
                <p>
                  We've sent a 6-digit verification code to <strong>{email}</strong>.
                  Enter it below to confirm your account.
                </p>
                <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0.25rem 0 0' }}>
                  📬 Can't find it? Check your <strong>spam or junk folder</strong>.
                </p>
              </div>

              <form onSubmit={handleVerify} className="is-form" noValidate>

                <div className={`is-field${step2Errors.code ? ' is-field--err' : ''}`}>
                  <label htmlFor="is-code">Verification Code *</label>
                  <input
                    id="is-code"
                    type="text"
                    value={code}
                    onChange={e => {
                      setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6));
                      setStep2Errors({});
                    }}
                    placeholder=""
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="is-code-input"
                  />
                  {step2Errors.code && <span className="is-err">{step2Errors.code}</span>}
                </div>

                {serverError && <div className="is-server-error">⚠ {serverError}</div>}

                <button type="submit" className="is-btn is-btn--primary" disabled={step2Loading}>
                  {step2Loading ? <><span className="is-spinner" /> Verifying…</> : 'Verify & Continue →'}
                </button>

                <div className="is-resend">
                  <span>Didn't receive the code?</span>
                  {resendCooldown > 0 ? (
                    <span className="is-resend-wait">Resend in {resendCooldown}s</span>
                  ) : (
                    <button type="button" className="is-resend-btn" onClick={handleResend}>
                      Resend Code
                    </button>
                  )}
                </div>

                <button type="button" className="is-btn is-btn--ghost" onClick={() => { setStep(1); setServerError(''); setCode(''); }}>
                  ← Change Email
                </button>

              </form>
            </>
          )}

          {/* ── STEP 3: Success / Redirecting ── */}
          {step === 3 && (
            <div className="is-success">
              <div className="is-success-icon">✅</div>
              <h2>Email Verified!</h2>
              <p>Your account is ready. Taking you to the application form now…</p>
              <div className="is-redirect-bar"><div className="is-redirect-fill" /></div>
            </div>
          )}

        </div>

        {/* Info panel */}
        <aside className="is-info">
          <h3>Why do I need an account?</h3>
          <ul>
            <li>
              <span className="is-info-icon">📋</span>
              <span>Track your application status from the instructor portal</span>
            </li>
            <li>
              <span className="is-info-icon">📝</span>
              <span>Update your details and upload new documents if requested</span>
            </li>
            <li>
              <span className="is-info-icon">🔔</span>
              <span>Receive real-time notifications about your application</span>
            </li>
            <li>
              <span className="is-info-icon">🔒</span>
              <span>Your information is securely stored and never shared</span>
            </li>
            <li>
              <span className="is-info-icon">🚀</span>
              <span>Once approved, log in to manage your student bookings</span>
            </li>
          </ul>
          <div className="is-info-note">
            <strong>Already applied?</strong> If you've already submitted an application, log in to the{' '}
            <a href="/instructor-login">Instructor Portal</a> to check your status.
          </div>
        </aside>

      </div>
    </div>
  );
}
